import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Drawer, Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Typography, Divider, Chip, Tooltip, Collapse, IconButton, ButtonBase, useMediaQuery, useTheme
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StorageIcon from '@mui/icons-material/Storage';
import BugReportIcon from '@mui/icons-material/BugReport';
import PeopleIcon from '@mui/icons-material/People';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CloudIcon from '@mui/icons-material/Cloud';
import RadarIcon from '@mui/icons-material/Radar';
import SettingsIcon from '@mui/icons-material/Settings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CircleIcon from '@mui/icons-material/Circle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { glassFallback } from '../../theme';

const PROVIDER_COLORS = { aws: '#ff9900', azure: '#0078d4', gcp: '#4285f4', all: '#9ca3af' };

export const COLLAPSED_WIDTH = 72;
const SIDEBAR_GLASS = 'linear-gradient(180deg, rgba(53,56,205,.82) 0%, rgba(45,49,166,.82) 100%)';
const SIDEBAR_SOLID = 'linear-gradient(180deg, #3538CD 0%, #2D31A6 100%)';
const ACTIVE_TEXT = '#3538CD';
export const ITEM_TEXT = 'rgba(255,255,255,.9)';
export const GROUP_TEXT = 'rgba(255,255,255,.88)';
const ICON = 'rgba(255,255,255,.7)';
const EASE = 'cubic-bezier(.2,.8,.2,1)';

function NavList({ sections, activePath, collapsed, onNavigate }) {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const itemRefs = useRef({});
  const [pill, setPill] = useState(null);
  const [animate, setAnimate] = useState(false);
  const [open, setOpen] = useState({});

  const activeGroup = sections.find((s) => s.items.some((i) => i.path === activePath))?.title;
  const activeShown = !!activeGroup && (collapsed || open[activeGroup] !== false);

  const measure = useCallback(() => {
    const el = itemRefs.current[activePath];
    const box = containerRef.current;
    if (!el || !box) return;
    const top = el.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
    const height = el.offsetHeight;
    setPill((p) => (p && p.top === top && p.height === height ? p : { top, height }));
  }, [activePath]);

  useLayoutEffect(measure, [measure, collapsed, open]);

  useEffect(() => {
    if (!pill || animate) return undefined;
    const id = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(id);
  }, [pill, animate]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !contentRef.current) return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(contentRef.current);
    return () => ro.disconnect();
  }, [measure]);

  return (
    <Box ref={containerRef} sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1.5, position: 'relative' }}>
      <Box
        aria-hidden="true"
        data-testid="nav-pill"
        sx={{
          position: 'absolute', left: 8, right: 8, top: 0, zIndex: 0,
          height: pill?.height || 0,
          borderRadius: '10px',
          bgcolor: 'common.white',
          boxShadow: '0 4px 14px rgba(16,24,40,.18)',
          opacity: pill && activeShown ? 1 : 0,
          transform: `translateY(${pill?.top || 0}px)`,
          transition: animate ? `transform 320ms ${EASE}, height 320ms ${EASE}, opacity 200ms` : 'none',
          willChange: 'transform',
          '&::after': {
            content: '""', position: 'absolute', right: -5, top: '50%', width: 10, height: 10, mt: '-5px',
            borderRadius: '50%', bgcolor: 'common.white'
          }
        }}
      />
      <Box ref={contentRef} sx={{ position: 'relative', zIndex: 1 }}>
        {sections.map((section) => {
          const groupOpen = collapsed || open[section.title] !== false;
          const groupId = `nav-group-${section.title.replace(/\W+/g, '-').toLowerCase()}`;
          return (
            <Box key={section.title} sx={{ mb: collapsed ? 0.5 : 1.5 }}>
              <ButtonBase
                onClick={() => setOpen((o) => ({ ...o, [section.title]: !groupOpen }))}
                aria-expanded={groupOpen}
                aria-controls={groupId}
                tabIndex={collapsed ? -1 : 0}
                sx={{
                  width: 'calc(100% - 16px)', mx: 1, px: 1.5, py: collapsed ? 0 : 0.75, borderRadius: '8px',
                  justifyContent: 'space-between', color: GROUP_TEXT,
                  opacity: collapsed ? 0 : 1, height: collapsed ? 8 : 'auto', overflow: 'hidden',
                  transition: 'opacity 200ms',
                  pointerEvents: collapsed ? 'none' : 'auto',
                  '&:hover': { bgcolor: 'rgba(255,255,255,.08)' },
                  '&.Mui-focusVisible': { outline: '2px solid #fff', outlineOffset: -2 }
                }}
              >
                <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                  {section.title}
                </Typography>
                <ExpandMoreIcon sx={{ fontSize: 16, transition: `transform 200ms ${EASE}`, transform: groupOpen ? 'none' : 'rotate(-90deg)' }} />
              </ButtonBase>
              <Collapse in={groupOpen} timeout={220} id={groupId}>
                <List disablePadding>
                  {section.items.map((item) => {
                    const active = item.path === activePath;
                    const button = (
                      <ListItemButton
                        ref={(el) => { itemRefs.current[item.path] = el; }}
                        onClick={() => onNavigate(item.path)}
                        aria-current={active ? 'page' : undefined}
                        aria-label={collapsed ? item.label : undefined}
                        sx={{
                          borderRadius: '10px', mx: 1, py: 0.85, px: collapsed ? '16px' : 1.5, minHeight: 40,
                          color: active ? ACTIVE_TEXT : ITEM_TEXT,
                          transition: 'color 200ms, padding 280ms',
                          '&:hover': { bgcolor: active ? 'transparent' : 'rgba(255,255,255,.08)' },
                          '&.Mui-focusVisible': { bgcolor: active ? 'transparent' : 'rgba(255,255,255,.12)', outline: '2px solid #fff', outlineOffset: -2 }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: active ? ACTIVE_TEXT : ICON, transition: 'color 200ms' }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          sx={{ opacity: collapsed ? 0 : 1, transition: 'opacity 200ms', whiteSpace: 'nowrap' }}
                          primaryTypographyProps={{ fontSize: 13.5, fontWeight: active ? 600 : 500, color: 'inherit' }}
                        />
                        {item.badge > 0 && (
                          <Tooltip title={item.badgeTooltip || ''} placement="right" arrow>
                            <Chip
                              size="small"
                              label={item.badge > 99 ? '99+' : item.badge}
                              color={item.badgeColor || 'default'}
                              sx={{
                                height: 20, fontSize: 10.5, fontWeight: 700, '& .MuiChip-label': { px: 1 },
                                opacity: collapsed ? 0 : 1, transition: 'opacity 200ms',
                                ...(item.badgeColor === 'default' && !active && { bgcolor: 'rgba(255,255,255,.16)', color: 'common.white' })
                              }}
                            />
                          </Tooltip>
                        )}
                      </ListItemButton>
                    );
                    return (
                      <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
                        {collapsed ? (
                          <Tooltip title={item.badge > 0 ? `${item.label} (${item.badge})` : item.label} placement="right" arrow>
                            {button}
                          </Tooltip>
                        ) : button}
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default function Sidebar({ drawerWidth, mobileOpen, onClose, collapsed = false, onToggleCollapse }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const findingsStats = useSelector((s) => s.cloud.findingsStats);
  const accounts = useSelector((s) => s.cloud.accounts);
  const selectedProvider = useSelector((s) => s.cloud.selectedProvider);
  const selectedAccountId = useSelector((s) => s.cloud.selectedAccountId);
  const accountsLookup = useSelector((s) => s.cloud.accountsLookup);

  const severityCount = (sev) => parseInt((findingsStats?.bySeverity || []).find((s) => s.severity === sev)?.count, 10) || 0;
  const criticalCount = severityCount('critical');
  const highCount = severityCount('high');
  const findingsBadge = criticalCount + highCount;
  const accountCount = (accounts || []).length;

  const selectedAccount = useMemo(
    () => (accountsLookup || []).find((a) => a.id === selectedAccountId) || null,
    [accountsLookup, selectedAccountId]
  );

  const sections = useMemo(() => [
    {
      title: 'Overview',
      items: [
        { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> }
      ]
    },
    {
      title: 'Inventory & Posture',
      items: [
        { label: 'Asset Inventory', path: '/assets', icon: <StorageIcon /> },
        {
          label: 'Findings',
          path: '/findings',
          icon: <BugReportIcon />,
          badge: findingsBadge,
          badgeColor: criticalCount > 0 ? 'error' : 'warning',
          badgeTooltip: `${criticalCount} critical · ${highCount} high`
        }
      ]
    },
    {
      title: 'Identity & Network',
      items: [
        { label: 'IAM Security', path: '/iam', icon: <PeopleIcon /> },
        { label: 'Network Security', path: '/network', icon: <NetworkCheckIcon /> },
        { label: 'Network Topology', path: '/network/topology', icon: <AccountTreeIcon /> }
      ]
    },
    {
      title: 'Compliance',
      items: [
        { label: 'Compliance', path: '/compliance', icon: <VerifiedUserIcon /> }
      ]
    },
    {
      title: 'Operations',
      items: [
        {
          label: 'Cloud Accounts',
          path: '/accounts',
          icon: <CloudIcon />,
          badge: accountCount,
          badgeColor: 'default'
        },
        { label: 'Scan History', path: '/scans', icon: <RadarIcon /> },
        { label: 'Reports', path: '/reports', icon: <AssessmentIcon /> }
      ]
    },
    {
      title: 'Configuration',
      items: [
        { label: 'Settings', path: '/settings', icon: <SettingsIcon /> }
      ]
    }
  ], [findingsBadge, criticalCount, highCount, accountCount]);

  const activePath = useMemo(() => {
    const all = sections.flatMap((s) => s.items.map((i) => i.path));
    const matches = all.filter((p) => {
      if (p === '/dashboard') {
        return location.pathname === '/' || location.pathname.startsWith('/dashboard');
      }
      return location.pathname === p || location.pathname.startsWith(p + '/');
    });
    return matches.sort((a, b) => b.length - a.length)[0] || null;
  }, [sections, location.pathname]);

  const onNavigate = (path) => {
    navigate(path);
    if (!isDesktop) onClose();
  };

  const drawerContent = (narrow) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', color: 'common.white' }}>
      <Box sx={{ p: narrow ? '20px 0' : 2.5, display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: narrow ? 'center' : 'flex-start', transition: 'padding 280ms' }}>
        <Box component="img" src="/sentrivox-symbol.webp" alt="" width={26} height={32} sx={{ display: 'block', flex: 'none' }} />
        <Box sx={{ minWidth: 0, flex: 1, display: narrow ? 'none' : 'block' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1, color: 'common.white', letterSpacing: 0.2 }}>
            Sentrivox
          </Typography>
          <Typography variant="caption" sx={{ color: ITEM_TEXT, fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }}>
            Cloud Security
          </Typography>
        </Box>
      </Box>

      {onToggleCollapse && (
        <IconButton
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          size="small"
          sx={{
            display: { xs: 'none', md: 'inline-flex' }, alignSelf: narrow ? 'center' : 'flex-end', mr: narrow ? 0 : 1.5, mt: -1, mb: 0.5,
            color: 'rgba(255,255,255,.85)', '&:hover': { bgcolor: 'rgba(255,255,255,.08)' }
          }}
        >
          <ChevronLeftIcon sx={{ transition: `transform 280ms ${EASE}`, transform: collapsed ? 'rotate(180deg)' : 'none' }} />
        </IconButton>
      )}

      <Divider sx={{ borderColor: 'rgba(255,255,255,.12)' }} />

      <NavList sections={sections} activePath={activePath} collapsed={narrow} onNavigate={onNavigate} />

      <Divider sx={{ borderColor: 'rgba(255,255,255,.12)' }} />
      <Box sx={{ p: 1.5, opacity: narrow ? 0 : 1, transition: 'opacity 200ms', whiteSpace: 'nowrap', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: selectedAccount ? 0.5 : 0 }}>
          <CircleIcon sx={{ fontSize: 9, color: PROVIDER_COLORS[selectedProvider] || PROVIDER_COLORS.all }} />
          <Typography variant="caption" sx={{ color: ITEM_TEXT, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {selectedProvider === 'all' ? 'All Providers' : String(selectedProvider).toUpperCase()}
          </Typography>
        </Box>
        {selectedAccount && (
          <Tooltip title={selectedAccount.account_id || selectedAccount.account_name} placement="top" arrow>
            <Typography
              variant="caption"
              sx={{
                display: 'block', pl: 2.25, color: 'common.white', fontSize: 11.5, fontWeight: 500,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
              }}
            >
              {selectedAccount.account_name || selectedAccount.account_id}
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  const paper = {
    boxSizing: 'border-box',
    background: SIDEBAR_GLASS,
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: 'none',
    boxShadow: 'none',
    color: 'common.white',
    ...glassFallback({ background: SIDEBAR_SOLID })
  };

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { ...paper, width: drawerWidth }
        }}
      >
        {drawerContent(false)}
      </Drawer>

      <Drawer
        variant="permanent"
        data-collapsed={collapsed ? 'true' : 'false'}
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            ...paper,
            width: collapsed ? COLLAPSED_WIDTH : drawerWidth,
            overflowX: 'hidden',
            transition: `width 280ms ${EASE}`,
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh'
          }
        }}
        open
      >
        {drawerContent(collapsed)}
      </Drawer>
    </>
  );
}
